import styled from '@emotion/styled';
import { useCallback, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { NewReport } from '../hooks/useNewReport';
import { EditableBlock } from '../definition/editor/EditableBlock';
import { InsertZone } from '../definition/editor/InsertZone';
import { useEditor } from '../definition/editor/OverviewEditorContext';
import { componentSpan, computeRows } from '../definition/editor/useOverviewEditor';
import { OverviewComponent } from './OverviewComponent';
import { hasAiSummaryData } from './AiSummary/summaryData';

function ReportOverview({ report }: { report: NewReport }) {
  const { t } = useTranslation('newReport');
  const editor = useEditor();
  const { controller, editing, actions, focusId } = editor;
  const rawData = report.rawData;
  const hasSummaryData = hasAiSummaryData(report);
  const components = useMemo(
    () =>
      (editing
        ? editor.components
        : editor.components.filter((component) => component.type !== 'aiSummary' || hasSummaryData)
      ).map((component) => {
        if (component.type !== 'question' || component.questionType) return component;
        const question = rawData?.questions.find((item) => item.index === component.questionIndex);
        return question ? { ...component, questionType: question.type } : component;
      }),
    [editor.components, editing, hasSummaryData, rawData],
  );
  const rows = useMemo(() => computeRows(components), [components]);
  const gridRef = useRef<HTMLDivElement>(null);
  const [draggingId, setDraggingId] = useState<string>();
  const [dropIndex, setDropIndex] = useState<number>();
  const endDrag = useCallback(() => {
    setDraggingId(undefined);
    setDropIndex(undefined);
  }, []);
  /** Insertion index of the row boundary nearest to the pointer. */
  const boundaryAt = (clientY: number): number => {
    const grid = gridRef.current;
    if (!grid || !rows.length) return components.length;
    const bounds = rows.map((row) => {
      const boxes = row.components
        .map((component) => grid.querySelector(`[data-component-id="${component.id}"]`)?.getBoundingClientRect())
        .filter((box): box is DOMRect => !!box);
      return { top: Math.min(...boxes.map((box) => box.top)), bottom: Math.max(...boxes.map((box) => box.bottom)) };
    });
    const candidates = [{ index: 0, y: bounds[0].top }];
    bounds.forEach((box, i) => {
      const next = bounds[i + 1];
      candidates.push({
        index: next ? rows[i + 1].start : components.length,
        y: next ? (box.bottom + next.top) / 2 : box.bottom,
      });
    });
    return candidates.reduce((best, candidate) =>
      Math.abs(candidate.y - clientY) < Math.abs(best.y - clientY) ? candidate : best,
    ).index;
  };
  const onDragOver = (event: DragEvent) => {
    if (!draggingId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    const index = boundaryAt(event.clientY);
    if (index !== dropIndex) setDropIndex(index);
  };
  const onDrop = (event: DragEvent) => {
    if (!draggingId) return;
    event.preventDefault();
    actions.dropAt(draggingId, dropIndex ?? components.length);
    endDrag();
  };

  const zone = (index: number, always = false) => (
    <InsertZone
      key={`zone-${index}`}
      index={index}
      actions={actions}
      canPlaceAiSummary={editor.canPlaceAiSummary}
      hasUnplacedQuestions={editor.unplacedQuestions.length > 0}
      active={dropIndex === index}
      always={always}
    />
  );
  const children: ReactNode[] = [];
  if (editing) children.push(zone(0, !components.length));
  rows.forEach((row) => {
    row.components.forEach((component, offset) => {
      const index = row.start + offset;
      const content = (
        <OverviewComponent
          report={report}
          component={component}
          actions={editing ? actions : undefined}
          autoFocus={editing && focusId === component.id}
        />
      );
      children.push(
        <Item key={component.id} span={componentSpan(component)} editing={editing}>
          {editing ? (
            <EditableBlock
              component={component}
              index={index}
              count={components.length}
              actions={actions}
              dragging={draggingId === component.id}
              onDragStart={setDraggingId}
              onDragEnd={endDrag}
            >
              {content}
            </EditableBlock>
          ) : (
            content
          )}
        </Item>,
      );
    });
    if (editing) children.push(zone(row.start + row.components.length));
  });

  return (
    <Container>
      {controller.validationError && (
        <p role="alert">
          {t('definition.invalid', { reason: t(`definition.validation.${controller.validationError}`) })}
        </p>
      )}
      <Grid ref={gridRef} editing={editing} onDragOver={onDragOver} onDrop={onDrop}>
        {children}
      </Grid>
    </Container>
  );
}

export default ReportOverview;

const Container = styled.div`
  max-width: 1000px;
  container-type: inline-size;
  container-name: overview;
`;
const Grid = styled.div<{ editing: boolean }>`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  column-gap: 16px;
  /* Edit mode replaces the row gaps and the outer padding with 32px insert zones, so nothing moves. */
  row-gap: ${({ editing }) => (editing ? 0 : '32px')};
  padding: ${({ editing }) => (editing ? 0 : '32px 0')};
  /* Cards sharing a row stretch to the tallest one. */
  align-items: stretch;
`;
const Item = styled.div<{ span: number; editing: boolean }>`
  grid-column: span ${({ span }) => span};
  min-width: 0;
  @container overview (max-width: 620px) {
    grid-column: span 12;
    margin-bottom: ${({ editing }) => (editing ? '16px' : 0)};
  }
`;
